
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { TabId } from '@/types/tabId';
import { getMaxScale } from '@/functions';
import BarChart from './BarChart';
import ChartMixin from './ChartMixin.vue';

@Component({
  components: {
    BarChart,
  },
})
export default class RCompareWaterChart extends Mixins(ChartMixin) {
  private chartOptions = null;

  get loading() {
    return this['$store'].getters.loading;
  }

  get tabId(): TabId {
    return this['$store'].getters.tabId;
  }

  get gridPitch() {
    return this['$store'].getters.gridPitch;
  }

  get isMobile() {
    return this['$store'].getters.isMobile;
  }

  get rerender() {
    return this['$store'].getters.rerender;
  }

  set rerender(value) {
    this['$store'].commit('setRerender', value);
  }

  get kouuScales() {
    return this['$store'].getters.kouuScales;
  }

  get kouuScale() {
    return this['$store'].getters.kouuScale === '自動' ? this.kouuAutoScale : this['$store'].getters.kouuScale;
  }

  get kouuAutoScale() {
    return this['$store'].getters.kouuAutoScale;
  }

  get ruikaScale() {
    return this['$store'].getters.ruikaScale === '自動' ? this['$store'].getters.ruikaAutoScale : this['$store'].getters.ruikaScale;
  }

  get ruikaAutoScale() {
    return this['$store'].getters.ruikaAutoScale;
  }

  get yskSpan() {
    return this['$store'].getters.yskSpan;
  }

  get targetTime() {
    return this['$store'].getters.targetTime;
  }

  get suiiResultList() {
    return this['$store'].getters.suiiResultList;
  }

  created() {
    window.addEventListener('resize', () => {
      this.updateResult();
    });
  }

  getOneFromSimResult(key) {
    const list: any[] = [];
    this.suiiResultList.forEach((result) => list.push(result.simResult.map((obj) => (Math.floor(obj[key]) <= -999 ? null : obj[key])).slice(0, 37 + 6 * this['$store'].getters.yskSpan)));
    return list;
  }

  getLinearArray(arrayList) {
    let list: number[] = [];
    arrayList.forEach((array) => {
      list = [...list, ...array];
    });
    return list;
  }

  @Watch('suiiResultList')
  @Watch('yskSpan')
  @Watch('rerender')
  public async updateResult(): Promise<void> {
    if (this.suiiResultList.length <= 0) return;
    if (this.tabId !== 'multiStations') return;

    const RArrayList = this.getOneFromSimResult('r');
    const RuikaArrayList = this.getOneFromSimResult('ruika');
    const dataTimeArrayList = this.getOneFromSimResult('datetime');
    const RMaxNum = Math.max(...this.getLinearArray(RArrayList), 5);
    const RuikaMaxNum = Math.max(...this.getLinearArray(RuikaArrayList), 5);

    this['$store'].commit('setKouuAutoScale', getMaxScale(RMaxNum));
    this['$store'].commit('setRuikaAutoScale', getMaxScale(RuikaMaxNum));

    const { stationsMap, colorMapArray } = this['$store'].getters;
    const stationIds = this['$store'].getters.currentStationIds;

    const datasets: any = [];

    for (let i = 0; i < stationIds.length; i += 1) {
      datasets.push(
        {
          type: 'bar',
          label: stationsMap[stationIds[i]].name,
          data: RArrayList[i],
          borderWidth: 1,
          borderColor: colorMapArray[i],
          style: `border-top-color: ${colorMapArray[i]};`,
          fixed: true,
          yAxisID: 'y-axis-1',
        },
      );
    }

    for (let i = 0; i < stationIds.length; i += 1) {
      datasets.push(
        {
          type: 'line',
          label: stationsMap[stationIds[i]].name,
          fill: false,
          data: RuikaArrayList[i],
          pointRadius: 0,
          borderWidth: 2,
          borderColor: colorMapArray[i],
          style: `border-top-color: ${colorMapArray[i]};`,
          yAxisID: 'y-axis-2',
        },
      );
    }

    this.chartData = {
      labels: dataTimeArrayList[0].slice(0, 37 + 6 * this['$store'].getters.yskSpan),
      datasets,
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.chartOptions = {
      responsive: true,
      // maintainAspectRatio: true!this.isMobile,
      maintainAspectRatio: false,
      tooltips: {
        callbacks: {
          label: (tooltipItem, obj) => `${obj.datasets[tooltipItem.datasetIndex].label}:${Number(tooltipItem.value).toFixed(1)}`,
        },
      },
      legend: {
        labels: {
          filter: () => false,
        },
      },
      annotation: {
        annotations: [{
          type: 'line',
          scaleID: 'x-axis-0',
          value: this.targetTime,
          borderWidth: 2,
          borderColor: 'gray',
        }],
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm',
              },
              stepSize: this.gridPitch,
            },
            gridLines: {
              color: '#999',
            },
            ticks: {
              fontSize: 14,
              fontColor: 'black',
            },
          },
          {
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: {
                day: 'MM/DD',
              },
            },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              fontSize: 14,
              fontColor: 'black',
            },
            gridLines: {
              color: '#999',
              drawTicks: false,
              drawBorder: false,
              drawOnChartArea: false,
              zeroLineColor: 'rgba(0, 0, 0, 0.1)',
            },
          },
        ],
        yAxes: [
          {
            afterFit(scaleInstance) {
              // eslint-disable-next-line no-param-reassign
              scaleInstance.width = 50;
            },
            id: 'y-axis-1',
            type: 'linear',
            position: 'left',
            display: true,
            gridLines: {
              color: '#999',
            },
            ticks: {
              fontSize: 14,
              fontColor: 'black',
              // maxTicksLimit: 6,
              // minLhTicksLimit: 6,
              // min: 0,
              // max: this.kouuScale === '自動' ? this.kouuAutoScale : this.kouuScale,

              // callback: (value) => {
              //   if (value % 1 !== 0) return null;
              //   const n = value % (this.kouuScale / 5);
              //   return n === 0 ? value : null;
              // },
              stepSize: (this.kouuScale / 5),
              // stepSize: 1,
              max: this.kouuScale,
            },
          },
          {
            afterFit(scaleInstance) {
            // eslint-disable-next-line no-param-reassign
              scaleInstance.width = 50;
            },
            id: 'y-axis-2',
            type: 'linear',
            position: 'right',
            display: true,
            afterBuildTicks: (scale) => {
              let num = this.ruikaScale;
              // eslint-disable-next-line no-param-reassign
              scale.ticks = [num];
              const step = this.ruikaScale / 5;
              while (scale.ticks.length <= 5) {
                num -= step;
                // eslint-disable-next-line no-param-reassign
                scale.ticks.push(num);
              }
            },
            gridLines: {
              color: '#999',
            },
            ticks: {
              // callback(value: any) { return ((value % 50) === 0) ? value : ''; },
              fontSize: 14,
              fontColor: 'black',
              maxTicksLimit: 6,
              min: 0,
              max: this.ruikaScale,
            },
          },
        ],
      },
    };
  }
}
